import { COLORS, type ColorType } from './types.js';

export type BossDifficulty = 'normal' | 'hard';
export type BossPhase = 1 | 2 | 3;

export interface BossRuntimeConfig {
  bossId: string;
  slug?: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  accentColor: string;
  difficulty: BossDifficulty;
  durationMs: number;
  hp: number;
  gripMax: number;
  staggerMax: number;
  attackIntervalMs: number;
  attackDamage: number;
  wrongTapDamage: number;
}

export type BossInputEvent =
  | { kind: 'tap'; atMs: number; color: ColorType }
  | { kind: 'shield'; atMs: number }
  | { kind: 'disrupt'; atMs: number };

export interface BossMovingTarget {
  index: number;
  color: ColorType;
  ageMs: number;
  windowMs: number;
  progress: number;
}

export interface BossSnapshot {
  elapsedMs: number;
  phase: BossPhase;
  targets: BossMovingTarget[];
  targetIndex: number;
  targetColor: ColorType;
  targetAgeMs: number;
  targetWindowMs: number;
  perfectFromMs: number;
  perfectToMs: number;
  score: number;
  perfectHits: number;
  resolvedTargets: number;
  perfectRate: number;
  damage: number;
  bossHpRemaining: number;
  grip: number;
  stagger: number;
  combo: number;
  bestCombo: number;
  attacksTaken: number;
  attacksBlocked: number;
  nextAttackInMs: number;
  shieldCooldownMs: number;
  disruptCooldownMs: number;
  shieldActive: boolean;
  vulnerable: boolean;
  stunned: boolean;
  victory: boolean;
  completed: boolean;
  defeated: boolean;
  victoryAtMs: number | null;
}

export const BOSS_SHIELD_DURATION_MS = 2_400;
export const BOSS_SHIELD_COOLDOWN_MS = 12_000;
export const BOSS_DISRUPT_DURATION_MS = 5_000;
export const BOSS_DISRUPT_COOLDOWN_MS = 14_000;
export const BOSS_STUN_DURATION_MS = 5_000;
export const BOSS_HIT_WINDOW_FROM = 0.36;
export const BOSS_HIT_WINDOW_TO = 0.7;
export const BOSS_PERFECT_SCORE = 100;

export type BossTimingResult = 'early' | 'hit' | 'late';
export type BossTapResult = BossTimingResult | 'wrong';

export interface BossTapJudgement {
  result: BossTapResult;
  targetIndex: number | null;
}

/** Shared timing judgement so client feedback and server replay stay identical. */
export function bossTimingResult(
  targetAgeMs: number,
  targetWindowMs: number,
): BossTimingResult {
  if (targetAgeMs < targetWindowMs * BOSS_HIT_WINDOW_FROM) return 'early';
  if (targetAgeMs <= targetWindowMs * BOSS_HIT_WINDOW_TO) return 'hit';
  return 'late';
}

function targetDistanceFromPerfect(target: BossMovingTarget): number {
  const perfectCenter = (BOSS_HIT_WINDOW_FROM + BOSS_HIT_WINDOW_TO) / 2;
  return Math.abs(target.progress - perfectCenter);
}

/**
 * Selects exactly one target for a tap. Matching targets inside the perfect
 * zone win first; otherwise the target currently crossing the zone is consumed.
 */
export function judgeBossTap(
  targets: readonly BossMovingTarget[],
  color: ColorType,
): BossTapJudgement {
  const ordered = [...targets].sort(
    (left, right) =>
      targetDistanceFromPerfect(left) - targetDistanceFromPerfect(right),
  );
  const matchingHit = ordered.find(
    (target) =>
      target.color === color
      && bossTimingResult(target.ageMs, target.windowMs) === 'hit',
  );
  if (matchingHit) {
    return { result: 'hit', targetIndex: matchingHit.index };
  }

  const crossing = ordered.find(
    (target) => bossTimingResult(target.ageMs, target.windowMs) === 'hit',
  );
  if (crossing) {
    return { result: 'wrong', targetIndex: crossing.index };
  }

  const matching = ordered.find((target) => target.color === color);
  if (matching) {
    return {
      result: bossTimingResult(matching.ageMs, matching.windowMs),
      targetIndex: matching.index,
    };
  }

  return {
    result: 'wrong',
    targetIndex: ordered[0]?.index ?? null,
  };
}

function mixed(seed: number, index: number): number {
  let x = (seed ^ Math.imul(index + 1, 0x9e3779b1)) | 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
}

/** Stable target generation shared by the client renderer and server replay. */
export function bossTargetColor(seed: number, index: number): ColorType {
  const current = mixed(seed, index) % COLORS.length;
  if (index >= 2) {
    const prev = mixed(seed, index - 1) % COLORS.length;
    const prev2 = mixed(seed, index - 2) % COLORS.length;
    if (current === prev && current === prev2) {
      return COLORS[(current + 1 + (mixed(seed, index + 7) % 3)) % COLORS.length]!;
    }
  }
  return COLORS[current]!;
}

export function bossPhase(elapsedMs: number, durationMs: number): BossPhase {
  const pct = elapsedMs / Math.max(1, durationMs);
  return pct >= 0.67 ? 3 : pct >= 0.34 ? 2 : 1;
}

export function bossTargetWindowMs(
  difficulty: BossDifficulty,
  phase: BossPhase,
): number {
  const normal = [0, 2_200, 1_950, 1_700] as const;
  const hard = [0, 1_950, 1_700, 1_500] as const;
  return (difficulty === 'hard' ? hard : normal)[phase];
}

/** Deterministic random spacing keeps rendering and settlement in sync. */
export function bossTargetGapMs(
  difficulty: BossDifficulty,
  seed: number,
  index: number,
): number {
  const min = difficulty === 'hard' ? 420 : 460;
  const spread = difficulty === 'hard' ? 171 : 191;
  return min + (mixed(seed ^ 0x51ed270b, index) % spread);
}

function attackInterval(config: BossRuntimeConfig, phase: BossPhase): number {
  const multiplier = phase === 1 ? 1 : phase === 2 ? 0.88 : 0.72;
  return Math.max(900, Math.round(config.attackIntervalMs * multiplier));
}

/**
 * Replays a run deterministically. Reward settlement must use this result and
 * never trust score/damage/grip/combo values submitted by the client.
 */
export function evaluateBossRun(
  config: BossRuntimeConfig,
  seed: number,
  inputEvents: readonly BossInputEvent[],
  elapsedMsRaw: number,
): BossSnapshot {
  const elapsedMs = Math.max(
    0,
    Math.min(config.durationMs, Math.floor(elapsedMsRaw)),
  );
  let score = 0;
  let perfectHits = 0;
  let resolvedTargets = 0;
  let damage = 0;
  let grip = config.gripMax;
  let stagger = 0;
  let combo = 0;
  let bestCombo = 0;
  let nextTargetIndex = 0;
  let nextTargetAt = 0;
  let attacksTaken = 0;
  let attacksBlocked = 0;
  let shieldUntil = -1;
  let disruptUntil = -1;
  let shieldReadyAt = 0;
  let disruptReadyAt = 0;
  let stunnedUntil = -1;
  let nextAttackAt = config.attackIntervalMs;
  let victoryAtMs: number | null = null;
  let activeTargets: Array<{
    index: number;
    color: ColorType;
    spawnedAt: number;
    windowMs: number;
  }> = [];

  const toMovingTarget = (
    target: (typeof activeTargets)[number],
    atMs: number,
  ): BossMovingTarget => {
    const ageMs = Math.max(0, atMs - target.spawnedAt);
    return {
      index: target.index,
      color: target.color,
      ageMs,
      windowMs: target.windowMs,
      progress: Math.min(1, ageMs / target.windowMs),
    };
  };

  const spawnTargetsUntil = (untilMs: number): void => {
    while (
      nextTargetAt <= untilMs
      && nextTargetAt <= config.durationMs
      && grip > 0
      && victoryAtMs == null
    ) {
      const index = nextTargetIndex;
      activeTargets.push({
        index,
        color: bossTargetColor(seed, index),
        spawnedAt: nextTargetAt,
        windowMs: bossTargetWindowMs(
          config.difficulty,
          bossPhase(nextTargetAt, config.durationMs),
        ),
      });
      nextTargetIndex += 1;
      nextTargetAt += bossTargetGapMs(
        config.difficulty,
        seed,
        nextTargetIndex,
      );
    }
  };

  const expireTargetsUntil = (untilMs: number): void => {
    let missedTargets = 0;
    activeTargets = activeTargets.filter((target) => {
      const expired = target.spawnedAt + target.windowMs <= untilMs;
      if (expired) missedTargets += 1;
      return !expired;
    });
    if (missedTargets > 0) {
      resolvedTargets += missedTargets;
      combo = 0;
    }
  };

  const applyAttacksUntil = (untilMs: number): void => {
    while (
      nextAttackAt <= untilMs
      && nextAttackAt <= config.durationMs
      && grip > 0
      && victoryAtMs == null
    ) {
      if (nextAttackAt < stunnedUntil || nextAttackAt <= shieldUntil) {
        attacksBlocked += 1;
      } else {
        grip = Math.max(0, grip - config.attackDamage);
        attacksTaken += 1;
      }
      nextAttackAt += attackInterval(
        config,
        bossPhase(nextAttackAt, config.durationMs),
      );
    }
  };

  for (const event of inputEvents) {
    const atMs = Math.max(0, Math.min(elapsedMs, Math.floor(event.atMs)));
    applyAttacksUntil(atMs);
    spawnTargetsUntil(atMs);
    expireTargetsUntil(atMs);
    if (grip <= 0 || victoryAtMs != null) break;

    if (event.kind === 'shield') {
      if (atMs >= shieldReadyAt) {
        shieldUntil = atMs + BOSS_SHIELD_DURATION_MS;
        shieldReadyAt = atMs + BOSS_SHIELD_COOLDOWN_MS;
      }
      continue;
    }
    if (event.kind === 'disrupt') {
      if (atMs >= disruptReadyAt) {
        disruptUntil = atMs + BOSS_DISRUPT_DURATION_MS;
        disruptReadyAt = atMs + BOSS_DISRUPT_COOLDOWN_MS;
      }
      continue;
    }

    const judgement = judgeBossTap(
      activeTargets.map((target) => toMovingTarget(target, atMs)),
      event.color,
    );
    if (judgement.targetIndex != null) {
      activeTargets = activeTargets.filter(
        (target) => target.index !== judgement.targetIndex,
      );
      resolvedTargets += 1;
    }

    if (judgement.result !== 'hit') {
      combo = 0;
      grip = Math.max(0, grip - config.wrongTapDamage);
      continue;
    }

    const comboBonus = combo >= 20 ? 5 : combo >= 10 ? 3 : combo >= 5 ? 1 : 0;
    const vulnerable = atMs <= disruptUntil;
    const stunned = atMs <= stunnedUntil;
    const baseDamage = Math.max(
      1,
      Math.round(config.hp * 0.0125 * (1 + comboBonus / 10)),
    );
    const multiplier = (vulnerable ? 1.5 : 1) * (stunned ? 2 : 1);

    perfectHits += 1;
    score += Math.max(
      BOSS_PERFECT_SCORE,
      Math.round((BOSS_PERFECT_SCORE + comboBonus * 10) * multiplier),
    );
    damage = Math.min(
      Math.max(0, config.hp - 1),
      damage + Math.max(1, Math.round(baseDamage * multiplier)),
    );
    stagger += 18;
    combo += 1;
    bestCombo = Math.max(bestCombo, combo);

    if (stagger >= config.staggerMax && atMs > stunnedUntil) {
      stagger = 0;
      stunnedUntil = atMs + BOSS_STUN_DURATION_MS;
    }
  }

  applyAttacksUntil(elapsedMs);
  spawnTargetsUntil(elapsedMs);
  expireTargetsUntil(elapsedMs);
  const phase = bossPhase(elapsedMs, config.durationMs);
  const targets = activeTargets.map((target) =>
    toMovingTarget(target, elapsedMs),
  );
  const primaryTarget = [...targets].sort(
    (left, right) =>
      targetDistanceFromPerfect(left) - targetDistanceFromPerfect(right),
  )[0];
  const targetWindowMs =
    primaryTarget?.windowMs ?? bossTargetWindowMs(config.difficulty, phase);
  const completed =
    victoryAtMs != null || (grip > 0 && elapsedMs >= config.durationMs);
  const defeated = grip <= 0;
  const perfectRate = resolvedTargets === 0
    ? 0
    : Math.round((perfectHits / resolvedTargets) * 100);

  return {
    elapsedMs,
    phase,
    targets,
    targetIndex: primaryTarget?.index ?? nextTargetIndex,
    targetColor:
      primaryTarget?.color ?? bossTargetColor(seed, nextTargetIndex),
    targetAgeMs: primaryTarget?.ageMs ?? 0,
    targetWindowMs,
    perfectFromMs: Math.round(targetWindowMs * BOSS_HIT_WINDOW_FROM),
    perfectToMs: Math.round(targetWindowMs * BOSS_HIT_WINDOW_TO),
    score,
    perfectHits,
    resolvedTargets,
    perfectRate,
    damage,
    bossHpRemaining: Math.max(0, config.hp - damage),
    grip,
    stagger,
    combo,
    bestCombo,
    attacksTaken,
    attacksBlocked,
    nextAttackInMs: Math.max(0, nextAttackAt - elapsedMs),
    shieldCooldownMs: Math.max(0, shieldReadyAt - elapsedMs),
    disruptCooldownMs: Math.max(0, disruptReadyAt - elapsedMs),
    shieldActive: elapsedMs <= shieldUntil,
    vulnerable: elapsedMs <= disruptUntil,
    stunned: elapsedMs <= stunnedUntil,
    victory: victoryAtMs != null,
    completed,
    defeated,
    victoryAtMs,
  };
}
